'use client';

import React, { useState, useEffect } from 'react';
import { Search, TrendingUp } from 'lucide-react';
import TrackCard from './TrackCard';
import TrendingSidebar from './TrendingSidebar';
import PulsePollWidget from './PulsePollWidget';
import { createClient } from '@/lib/supabase/client';

const genres = ['All', 'Electronic', 'Hip-Hop', 'Synthwave', 'R&B', 'Indie', 'Ambient', 'Trap', 'Lo-Fi', 'Pop'];

// Fallback demo tracks (shown when no DB tracks exist yet)
const demoTracks = [
  { id: 'track-001', title: 'Neon Drift', artist: 'Luna Voss', genre: 'Synthwave', plays: 142300, likes: 8420, comments: 312, votes: 1840, cover: 'LV', coverGradient: 'linear-gradient(135deg, #1B2A4A, #2A9D8F)', audioUrl: '', duration: '3:42', trending: true, new: false },
  { id: 'track-002', title: 'Static Rain', artist: 'Kai Rhythm', genre: 'Electronic', plays: 98700, likes: 5630, comments: 201, votes: 1240, cover: 'KR', coverGradient: 'linear-gradient(135deg, #2A9D8F, #1B2A4A)', audioUrl: '', duration: '4:18', trending: true, new: false },
  { id: 'track-003', title: 'Hollow Echo', artist: 'Marcus Flynn', genre: 'Ambient', plays: 67200, likes: 3890, comments: 98, votes: 876, cover: 'MF', coverGradient: 'linear-gradient(135deg, #10B981, #2A9D8F)', audioUrl: '', duration: '5:02', trending: false, new: true },
  { id: 'track-004', title: 'Chrome Pulse', artist: 'Zara Beats', genre: 'Trap', plays: 234100, likes: 12400, comments: 567, votes: 3120, cover: 'ZB', coverGradient: 'linear-gradient(135deg, #1B2A4A, #3BBFB0)', audioUrl: '', duration: '2:58', trending: true, new: false },
  { id: 'track-005', title: 'Midnight Protocol', artist: 'Dex Nova', genre: 'Hip-Hop', plays: 45600, likes: 2340, comments: 145, votes: 540, cover: 'DN', coverGradient: 'linear-gradient(135deg, #2A9D8F, #1B2A4A)', audioUrl: '', duration: '3:24', trending: false, new: true },
  { id: 'track-006', title: 'Signal Lost', artist: 'Priya Mash', genre: 'Indie', plays: 31200, likes: 1870, comments: 89, votes: 420, cover: 'PM', coverGradient: 'linear-gradient(135deg, #3BBFB0, #1B2A4A)', audioUrl: '', duration: '4:11', trending: false, new: true },
  { id: 'track-007', title: 'Digital Bloom', artist: 'Sol Waves', genre: 'Lo-Fi', plays: 88900, likes: 6120, comments: 234, votes: 1560, cover: 'SW', coverGradient: 'linear-gradient(135deg, #2A9D8F, #10B981)', audioUrl: '', duration: '3:55', trending: false, new: false },
  { id: 'track-008', title: 'Fracture Point', artist: 'Nyx Audio', genre: 'Electronic', plays: 119400, likes: 7230, comments: 398, votes: 2180, cover: 'NA', coverGradient: 'linear-gradient(135deg, #1B2A4A, #2A9D8F)', audioUrl: '', duration: '3:30', trending: true, new: false },
  { id: 'track-009', title: 'Velvet Circuit', artist: 'Tae Won', genre: 'R&B', plays: 54300, likes: 3200, comments: 167, votes: 780, cover: 'TW', coverGradient: 'linear-gradient(135deg, #EF4444, #A855F7)', duration: '4:44', trending: false, new: false },
  { id: 'track-010', title: 'Aurora Glitch', artist: 'Luna Voss', genre: 'Synthwave', plays: 76500, likes: 4890, comments: 223, votes: 1120, cover: 'LV', coverGradient: 'linear-gradient(135deg, #EC4899, #A855F7)', duration: '3:18', trending: false, new: false },
  { id: 'track-011', title: 'Bass Theory', artist: 'Kai Rhythm', genre: 'Trap', plays: 189200, likes: 10400, comments: 456, votes: 2640, cover: 'KR', coverGradient: 'linear-gradient(135deg, #F59E0B, #06B6D4)', duration: '2:46', trending: true, new: false },
  { id: 'track-012', title: 'Quiet Storm', artist: 'Priya Mash', genre: 'Ambient', plays: 23400, likes: 1240, comments: 54, votes: 310, cover: 'PM', coverGradient: 'linear-gradient(135deg, #10B981, #A855F7)', duration: '6:02', trending: false, new: true },
];

interface TrackRow {
  id: string;
  title: string;
  genre: string;
  audio_url: string;
  cover_url: string;
  duration_seconds: number;
  plays: number;
  likes_count: number;
  comments_count: number;
  votes_count: number;
  created_at: string;
  user_profiles: { full_name: string } | null;
}

function secondsToTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const gradients = [
  'linear-gradient(135deg, #1B2A4A, #2A9D8F)',
  'linear-gradient(135deg, #2A9D8F, #3BBFB0)',
  'linear-gradient(135deg, #1B2A4A, #3BBFB0)',
  'linear-gradient(135deg, #2A9D8F, #1B2A4A)',
  'linear-gradient(135deg, #10B981, #2A9D8F)',
];

export default function DiscoveryFeedContent() {
  const [search, setSearch] = useState('');
  const [activeGenre, setActiveGenre] = useState('All');
  const [sortBy, setSortBy] = useState<'trending' | 'new' | 'plays' | 'votes'>('trending');
  const [dbTracks, setDbTracks] = useState<any[]>([]);
  const [loadingDb, setLoadingDb] = useState(true);

  useEffect(() => {
    const fetchTracks = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('tracks')
          .select('*, user_profiles(full_name)')
          .eq('is_published', true)
          .order('created_at', { ascending: false })
          .limit(50);

        if (!error && data && data.length > 0) {
          // Also fetch live counts from junction tables for accuracy
          const trackIds = data.map((t: any) => t.id);
          const [likesCountRes, commentsCountRes] = await Promise.all([
            supabase
              .from('track_likes')
              .select('track_id')
              .in('track_id', trackIds),
            supabase
              .from('track_comments')
              .select('track_id')
              .in('track_id', trackIds)
              .is('parent_id', null),
          ]);

          // Build count maps from live data
          const likesMap: Record<string, number> = {};
          const commentsMap: Record<string, number> = {};
          (likesCountRes.data || []).forEach((r: any) => {
            likesMap[r.track_id] = (likesMap[r.track_id] || 0) + 1;
          });
          (commentsCountRes.data || []).forEach((r: any) => {
            commentsMap[r.track_id] = (commentsMap[r.track_id] || 0) + 1;
          });

          const mapped = (data as TrackRow[]).map((t, i) => ({
            id: t.id,
            title: t.title,
            artist: t.user_profiles?.full_name || 'Unknown Artist',
            genre: t.genre || 'Other',
            plays: t.plays,
            // Use live counts from junction tables (more accurate than cached columns)
            likes: likesMap[t.id] ?? t.likes_count,
            comments: commentsMap[t.id] ?? t.comments_count,
            votes: t.votes_count,
            cover: getInitials(t.user_profiles?.full_name || 'UN'),
            coverGradient: gradients[i % gradients.length],
            coverUrl: t.cover_url || '',
            audioUrl: t.audio_url || '',
            duration: t.duration_seconds > 0 ? secondsToTime(t.duration_seconds) : '—',
            trending: t.plays > 50000,
            new: new Date(t.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          }));
          setDbTracks(mapped);
        }
      } catch {
        // silently fall back to demo tracks
      } finally {
        setLoadingDb(false);
      }
    };
    fetchTracks();
  }, []);

  const allTracks = dbTracks.length > 0 ? dbTracks : demoTracks;

  const filtered = allTracks.filter(t => {
    const matchGenre = activeGenre === 'All' || t.genre === activeGenre;
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.artist.toLowerCase().includes(search.toLowerCase());
    return matchGenre && matchSearch;
  }).sort((a, b) => {
    if (sortBy === 'trending') return (b.trending ? 1 : 0) - (a.trending ? 1 : 0);
    if (sortBy === 'new') return (b.new ? 1 : 0) - (a.new ? 1 : 0);
    if (sortBy === 'plays') return b.plays - a.plays;
    if (sortBy === 'votes') return b.votes - a.votes;
    return 0;
  });

  const topTrack = filtered.find(t => t.trending) || filtered[0];

  return (
    <div className="flex gap-4 lg:gap-6">
      {/* Main feed */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Music Discovery</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              {filtered.length} tracks available
            </p>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="text"
                placeholder="Search tracks or artists..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-lg w-full sm:w-48 md:w-56"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-2 sm:px-3 py-2 text-sm rounded-lg flex-shrink-0"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
            >
              <option value="trending">Trending</option>
              <option value="new">New</option>
              <option value="plays">Most Played</option>
              <option value="votes">Most Voted</option>
            </select>
          </div>
        </div>

        {/* Genre chips */}
        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {genres.map((g) => (
            <button
              key={`genre-${g}`}
              onClick={() => setActiveGenre(g)}
              className="px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-150 flex-shrink-0"
              style={{
                background: activeGenre === g ? 'var(--secondary)' : 'var(--muted)',
                color: activeGenre === g ? 'white' : 'var(--muted-foreground)',
                border: activeGenre === g ? 'none' : '1px solid var(--border)',
              }}
            >
              {g}
            </button>
          ))}
        </div>

        {/* Featured trending banner */}
        {activeGenre === 'All' && !search && topTrack && (
          <div className="rounded-xl p-4 mb-6 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, rgba(42,157,143,0.12), rgba(27,42,74,0.2))', border: '1px solid rgba(42,157,143,0.25)' }}>
            <TrendingUp size={20} style={{ color: 'var(--secondary)' }} />
            <div>
              <p className="text-sm font-semibold">{topTrack.title} by {topTrack.artist} is trending</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {topTrack.plays.toLocaleString()} plays · {topTrack.votes.toLocaleString()} PULSE votes
              </p>
            </div>
            <span className="ml-auto badge-purple text-xs px-2 py-1 rounded-full font-semibold">#1 Today</span>
          </div>
        )}

        {/* Track grid */}
        {loadingDb ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={`skel-${i}`} className="skeleton rounded-xl h-64" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'var(--muted)' }}>
              <Search size={28} style={{ color: 'var(--muted-foreground)' }} />
            </div>
            <h3 className="font-semibold text-lg mb-2">No tracks found</h3>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Try adjusting your search or genre filter to discover more music.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((track) => (
              <TrackCard key={track.id} track={track} />
            ))}
          </div>
        )}
      </div>

      {/* Right sidebar */}
      <div className="hidden xl:flex flex-col gap-4 w-72 flex-shrink-0">
        <TrendingSidebar tracks={allTracks} />
        <PulsePollWidget />
      </div>
    </div>
  );
}